import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import '../../../features/cards/css/creator/mediaGrid.css';
import { ImagesIcon } from '../../../assets/Icons/SVG';
import {
  ROLE,
  MEDIA_TYPE,
  TAB_TYPE,
  PLACEHOLDER_POST_IMAGE
} from '../../../shared/constants/common';
import { formatDuration, isBundleContent, getMediaArray, getPrimaryMedia } from '../../../shared/utils/function';
import BundleSlider from '../../../features/slider/BundleSlider';
import LazyImage from '../../../components/LazyImage/LazyImage';
import VideoPlayer from '../../../shared/components/VideoPlayer';
import ViewContent from '../../../views/Modals/creator/ViewContent';
import MediaGridDropdown from './MediaGridDropdown';

const MediaGrid = ({
  postId = null,
  post,
  userRole = null,
  isCreatorOwnProfile = false,
  creatorInfo = null,
  tabType = TAB_TYPE.MEDIA,
  userId = null,
  user = null,
  openDropdownId = null,
  onDropdownToggle = null,
  onPostDeleted = null,
  onPostUpdated = null,
  isLock = false,
  isShowDuration = false,
  showControls = false,
  hideSliderDots = false,
  posts = [], // Array of all posts
  currentIndex = 0, // Current post index in the array
}) => {
  const isCreator = userRole === ROLE.CREATOR_SUB || userRole === ROLE.CREATOR_CONTENT;
  const [selectedPost, setSelectedPost] = useState(null);
  const [currentPostIndex, setCurrentPostIndex] = useState(currentIndex);
  const [showPostModal, setShowPostModal] = useState(false);

  // Update currentPostIndex when currentIndex prop changes
  useEffect(() => {
    setCurrentPostIndex(currentIndex);
  }, [currentIndex]);

  // Memoized bundle content detection
  const isBundle = useMemo(() => isBundleContent(post), [
    post.assets,
    post.type_file,
    post.type_content,
    post.content_type,
    post.isBundle
  ]);

  // Memoized media array for bundle content
  const mediaArray = useMemo(() => {
    return getMediaArray(post) || post.media || null;
  }, [
    post.assets,
    post.type_file,
    post.type_content,
    post.content_type,
    post.isBundle,
    post.media,
    post.caption
  ]);

  const primaryMedia = useMemo(() => getPrimaryMedia(post), [post.assets, post.cover_index, post.type_file]);

  const isPPVCreator = userRole === ROLE.CREATOR_CONTENT;
  const isMonthlyCreator = userRole === ROLE.CREATOR_SUB;
  const postPrice = parseFloat(post?.real_price || post?.price || 0);
  const showPPVPriceOverlay = isPPVCreator
    && isCreatorOwnProfile
    && tabType === TAB_TYPE.MEDIA
    && post.lock_content
    && postPrice > 0;
  const showMonthlyLockOverlay = isMonthlyCreator
    && isCreatorOwnProfile
    && tabType === TAB_TYPE.MEDIA
    && post.lock_content;

  // Memoized media statistics
  const stats = useMemo(() => {
    const mediaToCount = mediaArray || post.media || [];
    if (!mediaToCount.length) {
      return { imageCount: 0, videoCount: 0, totalDuration: '' };
    }

    const imageCount = mediaToCount.filter(m => m.type_file === MEDIA_TYPE.IMAGE).length;
    const videoCount = mediaToCount.filter(m => m.type_file === MEDIA_TYPE.VIDEO).length;
    const totalDuration = mediaToCount.reduce((total, m) => total + (m.duration || 0), 0);

    return {
      imageCount,
      videoCount,
      totalDuration: formatDuration(totalDuration)
    };
  }, [mediaArray, post.media]);

  const onView = useCallback((post) => {
    setSelectedPost(post);
    setCurrentPostIndex(currentIndex);
    setShowPostModal(true);
  }, [currentIndex]);

  const handleClosePostModal = useCallback(() => {
    setSelectedPost(null);
    setShowPostModal(false);
  }, []);

  const handleItemClick = useCallback(() => {
    onView?.(post);
  }, [onView, post]);

  return (
    <div className="creator-feed-item-wrapper" data-post-id={postId}>
      <div
        className="creator-feed-item"
        onClick={handleItemClick}
      >
        {/* Render bundle content with slider or single image/video */}
        <div className='creator-feed-item-content'>
          {isBundle && mediaArray?.length > 0 ? (
            <BundleSlider
              media={mediaArray}
              className="creator-feed-bundle-slider"
              showControls={showControls}
              isLock={isLock}
              isShowDuration={isShowDuration}
              hideSliderDots={hideSliderDots}
            />
            // <div>demo</div>
          ) : post.type_file === MEDIA_TYPE.VIDEO ? (
            <VideoPlayer
              src={primaryMedia.src}
              poster={primaryMedia.poster || null}
              className={`creator-feed-video hide_video_controls video-no-timeline ${post.lock_content ? 'video-locked' : ''}`}
              showControls={showControls}
              controlsList="nodownload noplaybackrate noremoteplayback"
              disablePictureInPicture
              disableRemotePlayback
              preventContextMenu
              muted
              loop
              playsInline
              preload="none"
              fallbackText='Video unavailable'
              showDurationBadge={!post.lock_content}
            />
          ) : (
            <LazyImage
              src={primaryMedia.src}
              alt={post.caption || 'Content'}
              onError={(e) => { e.target.src = PLACEHOLDER_POST_IMAGE; }}
            />
          )}
        </div>

        {/* Lock icon overlay - shown for locked preview, monthly lock, or PPV price on own profile */}
        {(isLock || showPPVPriceOverlay || showMonthlyLockOverlay) && (
          <div className={`creator-feed-lock-icon ${showPPVPriceOverlay ? 'with_amount' : ''}`}>
            <i className='fas fa-lock'></i>
            {showPPVPriceOverlay && (
              <span>
                ${postPrice % 1 === 0 ? postPrice : postPrice.toFixed(2)}
              </span>
            )}
          </div>
        )}

        {/* Media stats */}
        {isBundle && mediaArray?.length > 0 && (
          <div className="creator-feed-stats">
            <div className="creator-feed-stats-content">
              {stats.imageCount > 0 && (
                <>
                  {tabType === TAB_TYPE.MEDIA && (
                    <span>{stats.imageCount}</span>
                  )}

                  {tabType === TAB_TYPE.MEDIA && <i className="fa-solid fa-image"></i>}
                  {tabType === TAB_TYPE.FREE &&
                    <>
                      {stats.imageCount > 1 ? (
                        <ImagesIcon />
                      ) : (
                        <i className="fa-solid fa-image"></i>
                      )}
                    </>
                  }
                </>
              )}
              {stats.videoCount > 0 && (
                <>
                  {stats.imageCount > 0 && <span>•</span>}
                  {tabType === TAB_TYPE.MEDIA && (
                    <span>{stats.videoCount}</span>
                  )}
                  <i className="fa-solid fa-video"></i>

                  {stats.totalDuration && (
                    <>
                      <span>•</span>
                      <span>{stats.totalDuration}</span>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {/* Single media stats */}
        {!isBundle && (post.type_file === MEDIA_TYPE.VIDEO || post.type_file === MEDIA_TYPE.IMAGE) && (
          <div className="creator-feed-stats">
            <div className="creator-feed-stats-content">
              {post.type_file === MEDIA_TYPE.IMAGE && (
                <>
                  <i className="fa-solid fa-image"></i>
                  {/* {tabType === TAB_TYPE.MEDIA && <i className="fa-solid fa-image"></i>}
                  {tabType === TAB_TYPE.FREE && <i className="fa-solid fa-camera"></i>} */}
                </>
              )}
              {post.type_file === MEDIA_TYPE.VIDEO && (
                <>
                  <i className="fa-solid fa-video"></i>
                  {post.duration && <span>{formatDuration(post.duration)}</span>}
                </>
              )}
            </div>
          </div>
        )}

        {/* Fire icon for trending content */}
        {userRole === ROLE.CREATOR_CONTENT && post.isTrending && (
          <i className="fa-solid fa-fire creator-feed-fire-icon" aria-label="Trending content" />
        )}

        {/* Play icon for unlocked videos */}
        {/* {post.type_file === MEDIA_TYPE.VIDEO && !post.lock_content && (
          <i className="fa-solid fa-play video_play_icon" aria-label="play" />
        )} */}

        {/* Three-dot menu - only show for creator's own profile */}
        {isCreatorOwnProfile && isCreator && (
          <MediaGridDropdown
            className="icon-container media-grid-creator-dropdown"
            iconClass="fa-ellipsis-vertical"
            post={post}
            userRole={userRole}
            isShowPin={false}
            isOpen={openDropdownId === (post._id || post.id)}
            onToggle={onDropdownToggle}
            onDeleted={() => onPostDeleted?.(post._id || post.id)}
            onUpdated={(action, postId, newValue) => onPostUpdated?.(action, postId, newValue)}
          />
        )}

        {/* Gradient overlay */}
        <div className="creator-feed-gradient-overlay"></div>
      </div>

      {/* Post Modal */}
      {showPostModal && (
        <ViewContent
          post={selectedPost}
          handleClosePostModal={handleClosePostModal}
          creatorInfo={creatorInfo || user}
          userRole={userRole}
          isDarkMode={true}
          showControls={true}
          isShowDuration={true}
          posts={posts}
          currentIndex={currentPostIndex}
          onNavigate={(newIndex) => {
            if (newIndex >= 0 && newIndex < posts.length) {
              setCurrentPostIndex(newIndex);
              setSelectedPost(posts[newIndex]);
            }
          }}
          onDeleted={() => onPostDeleted?.(post._id || post.id)}
          onUpdated={(action, postId, newValue) => onPostUpdated?.(action, postId, newValue)}
        />
      )}
    </div>
  );
};

MediaGrid.propTypes = {
  postId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  posts: PropTypes.array, // Array of all posts
  currentIndex: PropTypes.number, // Current post index
  post: PropTypes.shape({
    type_file: PropTypes.string,
    type_content: PropTypes.string,
    content_type: PropTypes.string,
    isBundle: PropTypes.bool,
    caption: PropTypes.string,
    isTrending: PropTypes.bool,
    assets: PropTypes.arrayOf(PropTypes.shape({
      key: PropTypes.string,
      type: PropTypes.string,
      duration: PropTypes.number,
    })),
  }).isRequired,
  userRole: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isCreatorOwnProfile: PropTypes.bool,
  creatorInfo: PropTypes.object,
  tabType: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  userId: PropTypes.string,
  user: PropTypes.shape({
    _id: PropTypes.string,
    rol: PropTypes.number,
  }),
  openDropdownId: PropTypes.string,
  onDropdownToggle: PropTypes.func,
  onPostDeleted: PropTypes.func,
  onPostUpdated: PropTypes.func,
  isLock: PropTypes.bool,
  isShowDuration: PropTypes.bool,
  showControls: PropTypes.bool,
  hideSliderDots: PropTypes.bool,
};

const mapStateToProps = (state) => ({
  user: state.user?.user,
});

export default connect(mapStateToProps)(MediaGrid);
